import React from 'react';
import styles from './Layout.module.css';

/* ══════════════════════════════════════════════════════════════════
 * Screen — 페이지 최상위 컨테이너. 좌우 거터 20px 을 여기서만 겁니다.
 * ════════════════════════════════════════════════════════════════ */

interface ScreenProps {
    children: React.ReactNode;
    /** 하단 고정 버튼 바가 있으면 true */
    hasFooter?: boolean;
    /** 하단 GNB 가 있으면 true */
    hasGnb?: boolean;
    /** 상단 고정 헤더 등 거터 밖에 놓을 요소 */
    top?: React.ReactNode;
}

export const Screen = ({ children, hasFooter, hasGnb, top }: ScreenProps) => (
    <div
        className={[
            styles.screen,
            hasFooter ? styles.screenWithFooter : '',
            hasGnb ? styles.screenWithGnb : '',
        ]
            .filter(Boolean)
            .join(' ')}
    >
        {top}
        <div className={styles.body}>{children}</div>
    </div>
);

/** 좌우 거터를 무효화해 전체 폭을 쓰는 영역 (이미지 슬라이드, 지도 등) */
export const Bleed = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.bleed}>{children}</div>
);

/* ══════════════════════════════════════════════════════════════════
 * Section — 섹션 사이 간격을 컴포넌트가 책임집니다.
 * ════════════════════════════════════════════════════════════════ */

interface SectionProps {
    children: React.ReactNode;
    /** tight=40px · 기본 60px */
    spacing?: 'tight' | 'normal';
    /** 낮은 면으로 구분 (그림자 대신) */
    sunken?: boolean;
    /** 위쪽에 8px 회색 띠를 그어 앞 섹션과 잘라 냅니다 */
    divided?: boolean;
    /** 화면 마지막 섹션 — 아래 여백을 52px 로 키웁니다 (참고 앱과 동일) */
    last?: boolean;
}

export const Section = ({ children, spacing = 'normal', sunken, divided, last }: SectionProps) => (
    <section
        className={[
            sunken ? styles.sectionSunken : styles.section,
            spacing === 'tight' && !sunken && !divided ? styles.sectionTight : '',
            divided && !sunken ? styles.sectionDivided : '',
            last ? styles.sectionLast : '',
        ]
            .filter(Boolean)
            .join(' ')}
    >
        {children}
    </section>
);

export const Divider = ({ thick }: { thick?: boolean }) => (
    <hr className={thick ? styles.dividerThick : styles.divider} />
);

/**
 * 카드 목록 컨테이너. 카드 사이 간격을 컨테이너가 책임집니다.
 * 앱 폭 상한(560px) 안에서는 항상 1열 — 참고 앱의 모바일 레이아웃 그대로입니다.
 */
export const CardGrid = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.cardGrid}>{children}</div>
);

/* ══════════════════════════════════════════════════════════════════
 * SectionHeader
 *
 * 참고 앱은 제목 한 줄(명사)이지만, 모이는 copyTone 이 만드는 문장을
 * 담아야 하므로 헤드라인 + 서브카피 2줄 구조를 기본으로 둡니다.
 * 개수는 회색으로 — 브랜드색은 시간의 몫입니다.
 * ════════════════════════════════════════════════════════════════ */

interface SectionHeaderProps {
    title: React.ReactNode;
    /** 제목 옆 개수 — 회색으로 표시됩니다 */
    count?: number;
    /** 부제 — copyTone 문구가 들어가는 자리 */
    sub?: React.ReactNode;
    /** 우측 액션 라벨 (예: "전체보기") — i18n 으로 넘깁니다 */
    moreLabel?: string;
    onMore?: () => void;
    /** 화면 최상단 헤드라인이면 lg */
    size?: 'md' | 'lg';
    /** 헤더와 콘텐츠 사이 간격 포함 여부 */
    gap?: boolean;
}

const ChevronRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

export const SectionHeader = ({
    title, count, sub, moreLabel, onMore, size = 'md', gap = true,
}: SectionHeaderProps) => (
    <div className={gap ? `${styles.header} ${styles.headerGap}` : styles.header}>
        <div className={styles.headerText}>
            <h2 className={size === 'lg' ? `${styles.title} ${styles.titleLg}` : styles.title}>
                {title}
                {typeof count === 'number' && <span className={styles.count}>{count}</span>}
            </h2>
            {sub ? <p className={styles.sub}>{sub}</p> : null}
        </div>
        {moreLabel && onMore ? (
            <button type="button" className={styles.more} onClick={onMore}>
                {moreLabel}
                <ChevronRight />
            </button>
        ) : null}
    </div>
);
